import React, { useEffect, useState } from "react";

import {
  ArrowLeft,
  AlertTriangle,
  Box,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Loader2,
  Package,
  RefreshCw,
  Search,
  Sparkles,
  XCircle,
  Zap,
} from "lucide-react";

import { useProductTypeSync } from "@/hooks/useProductTypeSync";
import { cn } from "@/lib/utils";

import { Button, Card, CardContent, CardHeader, CardTitle } from "../../ui";
import { CanonicalCategoryMapping } from "../CanonicalCategoryMapping";
import { LogViewer } from "../LogViewer";
import { SyncProgressViewer } from "../SyncProgressViewer";
import {
  card,
  digestPreview,
  panelBorder,
  panelHeader,
  resultRow,
  resultRowHover,
  resultRowSelected,
  searchInput,
  selectedInfoCard,
} from "./ProductTypeSync.styles";
import type { ProductTypeSyncProps } from "./ProductTypeSync.types";

export const ProductTypeSync: React.FC<ProductTypeSyncProps> = ({
  onSelectedProductTypeChange,
  onSyncLogsChange,
  onSyncStatusChange,
  marketplaceCode,
  spApiMarketplaceId,
  initialProductType,
  initialCanonicalCategory,
}) => {
  const {
    query,
    setQuery,
    setSearched,
    searched,
    searchKeyword,
    handleSearch,
    results,
    searching,
    searchError,
    selectedType,
    handleSelect,
    schemaDigest,
    digestLoading,
    digestError,
    digestExpanded,
    setDigestExpanded,
    syncRunning,
    syncDone,
    syncError,
    syncConnectionWarning,
    startSyncPending,
    steps,
    logs,
    triggerSync,
    handleReset,
    strategy,
    refreshDirectory,
    refreshingDirectory,
    canRefreshDirectory,
    marketplaceCode: resolvedMarketplaceCode,
  } = useProductTypeSync({
    onSelectedProductTypeChange,
    onSyncLogsChange,
    marketplaceCode,
    spApiMarketplaceId,
    initialProductType,
  });
  const isShopifyMarketplace =
    resolvedMarketplaceCode.toUpperCase() === "SHOPIFY";

  useEffect(() => {
    onSyncStatusChange?.(syncRunning, syncDone);
  }, [syncRunning, syncDone, onSyncStatusChange]);

  // Elapsed sync clock: resets when a run starts, freezes at the final
  // duration when the run completes or fails.
  const syncActive = startSyncPending || syncRunning;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (!syncActive) return;
    const startedAt = Date.now();
    setElapsedSeconds(0);
    const id = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [syncActive]);

  const formatElapsed = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const mm = String(m).padStart(2, "0");
    const ss = String(s).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  // Prefer whichever digest object actually has content — some providers
  // return an EMPTY aiResponse object, which must not mask the real
  // marketplace schema sitting in canonicalSchemaPayload.
  const hasDigestEntries = (value: unknown): value is Record<string, unknown> =>
    value !== null &&
    typeof value === "object" &&
    Object.keys(value).length > 0;

  const digestDetails = hasDigestEntries(schemaDigest?.aiResponse)
    ? (schemaDigest.aiResponse as Record<string, unknown>)
    : hasDigestEntries(schemaDigest?.canonicalSchemaPayload)
      ? (schemaDigest.canonicalSchemaPayload as Record<string, unknown>)
      : null;

  const isPrimitiveDigest = (value: unknown) =>
    value != null && typeof value !== "object";
  const primitiveDigest = isPrimitiveDigest(schemaDigest?.aiResponse)
    ? schemaDigest?.aiResponse
    : isPrimitiveDigest(schemaDigest?.canonicalSchemaPayload)
      ? schemaDigest?.canonicalSchemaPayload
      : null;

  const formatDigestValue = (value: unknown) => {
    if (typeof value === "object" && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // ── Progress view — replaces the card entirely once sync starts ───────────
  if (startSyncPending || syncRunning || syncDone || syncError) {
    return (
      <Card data-testid="product-type-sync-section" className={card}>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors shrink-0"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base truncate">
                {selectedType?.displayName ?? selectedType?.name}
              </CardTitle>
              {!isShopifyMarketplace && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                  {selectedType?.name} · {resolvedMarketplaceCode}
                </p>
              )}
            </div>
            <div className="shrink-0">
              {syncError ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400">
                  <XCircle className="w-3 h-3" />
                  Failed
                </span>
              ) : syncDone ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                  Complete
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-indigo-100 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {startSyncPending ? "Starting…" : "Running"}
                </span>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Schema Digest */}
          <div>
            <button
              type="button"
              className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-2"
              onClick={() => setDigestExpanded((v) => !v)}
            >
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-indigo-400" />
                Canonical Schema Digest
              </span>
              {digestLoading ? (
                <Loader2 className="w-3 h-3 animate-spin text-indigo-400" />
              ) : digestExpanded ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>

            {digestLoading && digestExpanded && (
              <div className="flex items-center gap-2 px-2 py-3 rounded-md bg-slate-50 dark:bg-slate-800/50">
                <Loader2 className="w-3.5 h-3.5 text-indigo-400 animate-spin shrink-0" />
                <span className="text-xs text-slate-400">
                  Fetching canonical schema…
                </span>
              </div>
            )}
            {digestError && !digestLoading && (
              <div className="flex items-center gap-2 px-2 py-2 rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800">
                <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                <span className="text-xs text-rose-600 dark:text-rose-400">
                  {digestError}
                </span>
              </div>
            )}
            {schemaDigest && !digestLoading && digestExpanded && (
              <div className={digestPreview}>
                {digestDetails &&
                  Object.entries(digestDetails)
                    .slice(0, 20)
                    .map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <span className="text-indigo-400 shrink-0">{k}:</span>
                        <span
                          className={cn(
                            "text-slate-300 min-w-0",
                            typeof v === "object" && v !== null
                              ? "whitespace-pre-wrap break-all"
                              : "truncate",
                          )}
                        >
                          {formatDigestValue(v)}
                        </span>
                      </div>
                    ))}
                {!digestDetails && primitiveDigest != null && (
                  <span className="text-slate-300">
                    {JSON.stringify(primitiveDigest, null, 2)}
                  </span>
                )}
                {!digestDetails && primitiveDigest == null && (
                  <span className="text-slate-500">
                    No schema digest details available
                  </span>
                )}
              </div>
            )}
            {schemaDigest && !digestLoading && !digestExpanded && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                <span className="text-[11px] text-emerald-700 dark:text-emerald-400">
                  Schema digest loaded — click to expand
                </span>
              </div>
            )}
          </div>

          {/* Sync progress */}
          <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 overflow-hidden">
            <div className="flex items-center gap-2.5 px-4 py-3 bg-indigo-50 dark:bg-indigo-950/40 border-b border-indigo-200 dark:border-indigo-800">
              {startSyncPending || syncRunning ? (
                <Loader2 className="w-4 h-4 text-indigo-500 animate-spin shrink-0" />
              ) : syncError ? (
                <XCircle className="w-4 h-4 text-rose-500 shrink-0" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              )}
              <span
                className={cn(
                  "text-sm font-semibold",
                  syncError
                    ? "text-rose-700 dark:text-rose-300"
                    : "text-indigo-700 dark:text-indigo-300",
                )}
              >
                {startSyncPending
                  ? "Starting sync…"
                  : syncRunning
                    ? "Sync in progress…"
                    : syncError
                      ? "Sync failed"
                      : "Sync complete"}
              </span>
              <span className="ml-auto flex items-center gap-2">
                {syncDone && (
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    Session complete — results ready below
                  </span>
                )}
                <span
                  data-testid="sync-elapsed-timer"
                  className={cn(
                    "text-xs font-mono tabular-nums",
                    syncError
                      ? "text-rose-500 dark:text-rose-400"
                      : syncDone
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-indigo-500 dark:text-indigo-400",
                  )}
                >
                  {formatElapsed(elapsedSeconds)}
                </span>
              </span>
            </div>
            <div className="p-4 space-y-4 bg-white dark:bg-slate-900">
              {syncConnectionWarning && !syncError && (
                <div
                  data-testid="sync-connection-warning"
                  className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3"
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 w-4 h-4 text-amber-500 shrink-0" />
                    <p className="text-xs text-amber-700 dark:text-amber-200">
                      {syncConnectionWarning}
                    </p>
                  </div>
                </div>
              )}
              {syncError && (
                <div
                  data-testid="sync-error-alert"
                  className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 p-3"
                >
                  <div className="flex items-start gap-2">
                    <XCircle className="mt-0.5 w-4 h-4 text-rose-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">
                        Marketplace sync stopped
                      </p>
                      <p className="mt-1 text-xs text-rose-700 dark:text-rose-200 break-words">
                        {syncError}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={triggerSync}>
                      <RefreshCw className="w-3.5 h-3.5" />
                      Retry Sync
                    </Button>
                  </div>
                </div>
              )}
              <SyncProgressViewer steps={steps} />
              {logs.length > 0 && <LogViewer logs={logs} maxHeight="200px" />}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Default view ──────────────────────────────────────────────────────────
  return (
    <Card data-testid="product-type-sync-section" className={card}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-indigo-100 dark:bg-indigo-900/50">
            <Zap className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <CardTitle className="text-base">
              Sync Specific Product Type
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Search product type and sync only selected category instead of
              full marketplace.
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="product-type-search-input"
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSearched(false);
              }}
              onKeyDown={handleKeyDown}
              placeholder={`Search product type (e.g. TShirt, Bracelet...)`}
              className={searchInput}
            />
          </div>
          <Button
            data-testid="product-type-search-btn"
            size="sm"
            onClick={handleSearch}
            disabled={!query.trim()}
          >
            <Search className="w-4 h-4" />
            Search
          </Button>
          {strategy.syncMarketplace === "flipkart" && (
            <Button
              data-testid="flipkart-refresh-directory-btn"
              size="sm"
              variant="outline"
              onClick={refreshDirectory}
              disabled={refreshingDirectory || !canRefreshDirectory}
              title="Re-import Flipkart categories from the connected seller"
            >
              {refreshingDirectory ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Refresh categories
            </Button>
          )}
        </div>

        {searched && (
          <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div data-testid="search-results-panel" className={panelBorder}>
              <div className={panelHeader}>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Search Results
                  {!searching && (
                    <span className="ml-1.5 text-slate-400 dark:text-slate-500 font-normal">
                      ({results.length})
                    </span>
                  )}
                </p>
              </div>
              <div className="max-h-[260px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                {searching ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-2">
                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                    <p className="text-sm text-slate-400 dark:text-slate-500">
                      Searching {resolvedMarketplaceCode} product types...
                    </p>
                  </div>
                ) : searchError ? (
                  <div
                    data-testid="search-error-state"
                    className="flex flex-col items-center justify-center py-10 text-center px-4"
                  >
                    <XCircle className="w-8 h-8 text-rose-400 mb-2" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Search failed
                    </p>
                    <p className="text-xs text-rose-500 mt-0.5">
                      {searchError}
                    </p>
                  </div>
                ) : results.length === 0 ? (
                  <div
                    data-testid="no-results-state"
                    className="flex flex-col items-center justify-center py-10 text-center"
                  >
                    <Box className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-2" />
                    <p className="text-sm text-slate-400 dark:text-slate-500">
                      No results found
                    </p>
                    <p className="text-xs text-slate-400 dark:text-slate-600 mt-0.5">
                      Try a different search term
                    </p>
                  </div>
                ) : (
                  results.map((pt) => {
                    const rowId = pt.categoryId ?? pt.name;
                    const isSelected =
                      (selectedType?.categoryId ?? selectedType?.name) ===
                      rowId;
                    const isSynced = !!pt.synced;
                    return (
                      <div
                        key={rowId}
                        data-testid={`search-result-${pt.name}`}
                        className={cn(
                          resultRow,
                          isSynced
                            ? "opacity-75 cursor-default bg-slate-50/50 dark:bg-slate-800/20"
                            : isSelected
                              ? resultRowSelected
                              : resultRowHover,
                        )}
                        onClick={() => !isSynced && handleSelect(pt)}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p
                              className={cn(
                                "text-sm font-semibold truncate",
                                isSelected
                                  ? "text-indigo-700 dark:text-indigo-300"
                                  : "text-slate-900 dark:text-slate-100",
                              )}
                            >
                              {pt.displayName}
                            </p>
                            {isSynced && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                                <CheckCircle2 className="w-3 h-3" />
                                Synced
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 truncate mt-0.5">
                            {pt.name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-3">
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                            {resolvedMarketplaceCode}
                          </span>
                          {isSelected ? (
                            <Check className="w-4 h-4 text-indigo-500" />
                          ) : isSynced ? null : (
                            <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600" />
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div data-testid="selected-type-panel" className={panelBorder}>
              <div className={panelHeader}>
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Selected Product Type
                </p>
              </div>
              {!selectedType ? (
                <div
                  data-testid="empty-selection-state"
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                    <Package className="w-6 h-6 text-slate-300 dark:text-slate-600" />
                  </div>
                  <p className="text-sm text-slate-400 dark:text-slate-500">
                    Select a product type to begin sync
                  </p>
                </div>
              ) : (
                <div className="p-3">
                  <div
                    data-testid="selected-type-info"
                    className={selectedInfoCard}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Product Type
                      </span>
                      <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                        {selectedType.displayName}
                      </span>
                    </div>
                    {!isShopifyMarketplace && (
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Code
                        </span>
                        <span className="font-mono text-xs text-indigo-600 dark:text-indigo-400">
                          {selectedType.name}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        Marketplace
                      </span>
                      <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                        {resolvedMarketplaceCode}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {selectedType && (
          <CanonicalCategoryMapping
            selectedProductType={selectedType}
            onSave={triggerSync}
            marketplace={resolvedMarketplaceCode}
            sourceQuery={searchKeyword}
            initialCategory={initialCanonicalCategory}
          />
        )}
      </CardContent>
    </Card>
  );
};
